import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/pages/home/components/Footer';
import { allProducts } from '@/mocks/products';

const popularTags = ['Skincare', 'Coffee Maker', 'Fitness Watch', 'Kitchen Set', 'Massage Gun', 'Earbuds', 'Air Fryer', 'LED Mirror'];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [inputValue, setInputValue] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('featured');
  const [priceRange, setPriceRange] = useState('all');

  useEffect(() => {
    const q = searchParams.get('q') || '';
    setQuery(q);
    setInputValue(q);
  }, [searchParams]);

  const results = useMemo(() => {
    let r = [...allProducts];
    if (query) {
      r = r.filter(p =>
        p.name.toLowerCase().includes(query.toLowerCase()) ||
        p.description.toLowerCase().includes(query.toLowerCase()) ||
        p.category.toLowerCase().includes(query.toLowerCase())
      );
    }
    if (selectedCategory !== 'all') r = r.filter(p => p.category === selectedCategory);
    switch (priceRange) {
      case '0-200': r = r.filter(p => p.price <= 200); break;
      case '200-500': r = r.filter(p => p.price > 200 && p.price <= 500); break;
      case '500+': r = r.filter(p => p.price > 500); break;
    }
    switch (sortBy) {
      case 'low-high': r.sort((a, b) => a.price - b.price); break;
      case 'high-low': r.sort((a, b) => b.price - a.price); break;
      case 'rating': r.sort((a, b) => b.rating - a.rating); break;
    }
    return r;
  }, [query, selectedCategory, sortBy, priceRange]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams({ q: inputValue });
  };

  const handleTagClick = (tag: string) => {
    setInputValue(tag);
    setSearchParams({ q: tag });
  };

  const handleAddToCart = (product: typeof allProducts[0]) => {
    const raw = localStorage.getItem('cart');
    const cart = raw ? JSON.parse(raw) : [];
    const existing = cart.find((i: { id: number }) => i.id === product.id);
    if (existing) { existing.quantity += 1; }
    else { cart.push({ ...product, quantity: 1 }); }
    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const categories = [
    { key: 'all', label: 'All' },
    { key: 'beauty', label: 'Beauty' },
    { key: 'health', label: 'Health' },
    { key: 'kitchen', label: 'Kitchen' },
    { key: 'electronics', label: 'Electronics' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50/30 via-white to-pink-50/30">
      <Header />

      <div className="pt-24 pb-8 px-4 sm:px-6" style={{ background: 'linear-gradient(135deg, #fff7ed, #fdf2f8)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent mb-6">
            Search Products
          </h1>
          <form onSubmit={handleSearch} className="relative max-w-2xl mx-auto">
            <i className="ri-search-line absolute left-4 sm:left-5 top-1/2 -translate-y-1/2 text-gray-400 text-lg sm:text-xl"></i>
            <input
              type="text"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              placeholder="Search for products, brands, categories..."
              className="w-full pl-12 sm:pl-14 pr-24 sm:pr-32 py-3 sm:py-4 rounded-full border border-gray-200 bg-white text-gray-800 focus:outline-none focus:border-orange-400 text-sm sm:text-base"
            />
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 px-4 sm:px-6 py-2 sm:py-2.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-600 text-white font-bold text-xs sm:text-sm hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
            >
              Search
            </button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Filter Pills */}
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-6">
          {/* Category Pills */}
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <button
                key={cat.key}
                onClick={() => setSelectedCategory(cat.key)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  selectedCategory === cat.key
                    ? 'bg-gradient-to-r from-orange-500 to-pink-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="w-px bg-gray-200 hidden md:block" />

          {/* Sort */}
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="px-4 py-2 rounded-full border border-gray-200 text-sm text-gray-700 focus:outline-none focus:border-orange-400 bg-white cursor-pointer"
          >
            <option value="featured">Sort: Featured</option>
            <option value="low-high">Price: Low to High</option>
            <option value="high-low">Price: High to Low</option>
            <option value="rating">Highest Rated</option>
          </select>

          {/* Price Range */}
          <select
            value={priceRange}
            onChange={e => setPriceRange(e.target.value)}
            className="px-4 py-2 rounded-full border border-gray-200 text-sm text-gray-700 focus:outline-none focus:border-orange-400 bg-white cursor-pointer"
          >
            <option value="all">All Prices</option>
            <option value="0-200">Under AED 200</option>
            <option value="200-500">AED 200–500</option>
            <option value="500+">Over AED 500</option>
          </select>
        </div>

        {/* Results Count */}
        {query && (
          <p className="text-sm text-gray-500 mb-6">
            {results.length > 0 ? (
              <>Showing <strong className="text-gray-800">{results.length}</strong> results for &quot;<strong className="text-orange-500">{query}</strong>&quot;</>
            ) : (
              <>No results for &quot;<strong className="text-orange-500">{query}</strong>&quot;</>
            )}
          </p>
        )}

        {/* Results Grid */}
        {results.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {results.map(product => {
              const discount = Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100);
              const badgeColors: Record<string, string> = {
                HOT: 'from-red-500 to-orange-500',
                SALE: 'from-orange-500 to-pink-600',
                NEW: 'from-green-500 to-teal-500',
              };
              return (
                <Link
                  key={product.id}
                  to={`/product/${product.id}`}
                  className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 overflow-hidden hover:border-orange-300 hover:scale-[1.02] transition-all duration-300 group block"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500" />
                    <span className={`absolute top-3 left-3 text-white text-xs font-bold px-3 py-1 rounded-full bg-gradient-to-r ${badgeColors[product.badge]}`}>
                      {product.badge}
                    </span>
                    <span className="absolute top-3 right-3 bg-white/90 text-green-600 text-xs font-bold px-2 py-1 rounded-full">-{discount}%</span>
                  </div>
                  <div className="p-4">
                    <p className="text-xs text-orange-500 font-medium capitalize mb-1">{product.category}</p>
                    <h3 className="font-bold text-gray-800 text-sm mb-2 line-clamp-2">{product.name}</h3>
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i key={i} className={`ri-star-${i < Math.floor(product.rating) ? 'fill' : 'line'} text-amber-400 text-xs`}></i>
                      ))}
                      <span className="text-xs text-gray-500 ml-1">({product.reviews})</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-base font-black text-gray-800">AED {product.price}</span>
                        <span className="text-xs text-gray-400 line-through ml-1">AED {product.originalPrice}</span>
                      </div>
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddToCart(product); }}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-r from-orange-500 to-pink-600 text-white hover:scale-110 transition-transform cursor-pointer"
                      >
                        <i className="ri-shopping-cart-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-24 h-24 flex items-center justify-center mx-auto mb-6 rounded-full bg-orange-50">
              <i className="ri-search-line text-5xl text-orange-300"></i>
            </div>
            <h3 className="text-2xl font-black text-gray-800 mb-3">No results found</h3>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">
              We couldn&apos;t find anything matching your search. Try different keywords or browse our popular categories.
            </p>

            {/* Suggestions */}
            <div className="mb-8">
              <p className="text-sm font-semibold text-gray-600 mb-3">Try searching for:</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {['Skincare Set', 'Smart Watch', 'Coffee Maker', 'Massage Gun', 'Air Fryer', 'Earbuds'].map(s => (
                  <button
                    key={s}
                    onClick={() => handleTagClick(s)}
                    className="px-4 py-2 rounded-full bg-white border border-gray-200 text-gray-600 text-sm hover:border-orange-400 hover:text-orange-500 transition-all cursor-pointer whitespace-nowrap"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Popular in Dubai */}
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-3 flex items-center justify-center gap-2">
                <i className="ri-map-pin-line text-orange-500"></i>
                Popular in Dubai
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {popularTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => handleTagClick(tag)}
                    className="px-4 py-2 rounded-full bg-gradient-to-r from-orange-50 to-pink-50 border border-orange-200 text-orange-600 text-sm font-medium hover:from-orange-500 hover:to-pink-600 hover:text-white transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-fire-line mr-1"></i>{tag}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
